import type { NoteSourceSentence } from "@rosc/contracts";

// Helpers for "Sources" (v3.2): which note sentence a click landed on, and
// which words of the transcript to highlight.

const WORD = /[\p{L}\p{N}]/u;

// Lower-case letters and digits only, with "[verify]" markers removed, and a
// map from each kept character back to its index in `text`.
export function foldWithMap(text: string): { folded: string; map: number[] } {
  const cleaned = text.replace(/\\?\[verify\\?\]/giu, (match) =>
    " ".repeat(match.length),
  );
  let folded = "";
  const map: number[] = [];
  let index = 0;
  for (const char of cleaned) {
    if (WORD.test(char)) {
      const lower = char.toLowerCase();
      folded += lower;
      for (let i = 0; i < lower.length; i++) map.push(index);
    }
    index += char.length;
  }
  return { folded, map };
}

export type SentenceHit = {
  sentence: NoteSourceSentence;
  // [start, end) in the block's text.
  start: number;
  end: number;
};

// The sentence of `blockText` (the rendered text of one paragraph or list
// item) at character `offset`. Sentences come from the API and are matched
// by their letters and digits, so Markdown, bullets and chips do not matter.
// Without a sentence under the offset, the nearest one in the block.
export function sentenceAt(
  blockText: string,
  offset: number,
  sentences: readonly NoteSourceSentence[],
): SentenceHit | null {
  const block = foldWithMap(blockText);
  if (!block.folded) return null;
  const hits: SentenceHit[] = [];
  for (const sentence of sentences) {
    const needle = foldWithMap(sentence.text).folded;
    if (needle.length < 2) continue;
    for (
      let at = block.folded.indexOf(needle);
      at >= 0;
      at = block.folded.indexOf(needle, at + 1)
    ) {
      const start = block.map[at] ?? 0;
      const last = block.map[at + needle.length - 1] ?? start;
      hits.push({ sentence, start, end: last + 1 });
    }
  }
  if (!hits.length) return null;
  const inside = hits.filter((hit) => hit.start <= offset && offset < hit.end);
  // The shortest sentence under the click wins (a sentence can contain
  // another's text).
  if (inside.length)
    return inside.reduce((a, b) => (b.end - b.start < a.end - a.start ? b : a));
  const distance = (hit: SentenceHit) =>
    offset < hit.start ? hit.start - offset : offset - hit.end + 1;
  return hits.reduce((a, b) => (distance(b) < distance(a) ? b : a));
}

// How well the rendered text of one block (paragraph, list item) is sourced:
// "all" when every note sentence in it has a source in the transcript,
// "some" when only some do, null when none does. The guided tour points at an
// "all" block first, so wherever the clinician taps it, the line opens a
// real source rather than "No source".
export type BlockSourceState = "all" | "some" | null;
const sourced = (sentence: NoteSourceSentence) =>
  sentence.supported && sentence.segments.length > 0;
export function blockSourceState(
  blockText: string,
  sentences: readonly NoteSourceSentence[],
): BlockSourceState {
  const block = foldWithMap(blockText).folded;
  if (!block) return null;
  const found = sentences
    .map((sentence) => ({ sentence, text: foldWithMap(sentence.text).folded }))
    .filter(({ text }) => text.length >= 2 && block.includes(text));
  // A sentence found only inside a longer sentence of the block ("Diabetes"
  // inside "Presents regarding diabetes...") is not one of its lines.
  const lines = found.filter(
    ({ text }) =>
      !found.some(
        (other) => other.text.length > text.length && other.text.includes(text),
      ),
  );
  const count = lines.filter(({ sentence }) => sourced(sentence)).length;
  if (!count) return null;
  return count === lines.length ? "all" : "some";
}

// Whether the block holds at least one sentence the transcript supports.
export function blockHasSupported(
  blockText: string,
  sentences: readonly NoteSourceSentence[],
): boolean {
  return blockSourceState(blockText, sentences) !== null;
}

const STOP = new Set([
  "the",
  "and",
  "for",
  "with",
  "you",
  "your",
  "are",
  "was",
  "has",
  "have",
  "had",
  "this",
  "that",
  "from",
  "but",
  "not",
  "per",
  "patient",
]);
const tokens = (text: string) =>
  (text.toLowerCase().match(/[\p{L}\p{N}]+(?:[.,][\p{N}]+)?/gu) ?? []).filter(
    (word) => /\p{N}/u.test(word) || (word.length >= 3 && !STOP.has(word)),
  );

export type HighlightPiece = { text: string; hit: boolean };

// The segment's text split into pieces, with the words the sentence shares
// marked (content words of 3+ letters, numbers, and words that start the
// same for 5+ letters, so "hypertensive" lights "hypertension"). Languages
// without spaces (Chinese, Japanese) mark shared runs of 2+ characters.
export function highlightShared(
  segmentText: string,
  sentenceText: string,
): HighlightPiece[] {
  const wanted = new Set(tokens(sentenceText));
  const stems = [...wanted].filter((word) => word.length >= 5);
  const pieces: HighlightPiece[] = [];
  let last = 0;
  const push = (text: string, hit: boolean) => {
    if (!text) return;
    const previous = pieces[pieces.length - 1];
    if (previous && previous.hit === hit) previous.text += text;
    else pieces.push({ text, hit });
  };
  for (const match of segmentText.matchAll(
    /[\p{L}\p{N}]+(?:[.,][\p{N}]+)?/gu,
  )) {
    const word = match[0].toLowerCase();
    const start = match.index ?? 0;
    const hit =
      wanted.has(word) ||
      (word.length >= 5 &&
        stems.some(
          (stem) =>
            stem.slice(0, 5) === word.slice(0, 5) && !/\p{N}/u.test(word),
        )) ||
      // Spaceless scripts: a run that occurs in the sentence.
      (/[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u.test(word) &&
        word.length >= 2 &&
        sentenceText.includes(match[0]));
    push(segmentText.slice(last, start), false);
    push(match[0], hit);
    last = start + match[0].length;
  }
  push(segmentText.slice(last), false);
  return pieces;
}

// 0:07, 2:14, 1:02:05
export function formatMoment(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = String(total % 60).padStart(2, "0");
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds}`
    : `${minutes}:${seconds}`;
}
